
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface UserRegistrationData {
  email: string;
  password: string;
  name: string;
}

export interface UserLoginData {
  email: string;
  password: string;
}

export const authService = {
  // Register a new user
  register: async ({ email, password, name }: UserRegistrationData) => {
    try {
      // 1. Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: name ? { full_name: name } : undefined,
        },
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error("Erro ao criar usuário no Auth");
      }

      // 2. Create user in the custom users table using the auth user's ID
      const userRecord = {
        id: authData.user.id,
        email,
        role: 'viewer',
      };

      const { error: userError } = await supabase
        .from('users')
        .upsert(userRecord, { onConflict: 'id' });

      if (userError) {
        // Log the error - cleanup should be handled by database triggers or scheduled jobs
        console.error('Failed to create user profile:', userError);
        throw new Error('Falha ao criar perfil do usuário. Por favor, contate o administrador.');
      }

      return { success: true, user: authData.user };
    } catch (error: any) {
      console.error("Registration error:", error);
      return { 
        success: false, 
        error: error.message || "Erro ao registrar usuário" 
      };
    }
  },

  // Login an existing user
  login: async ({ email, password }: UserLoginData) => {
    try {
      // Sign in with email and password
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      if (!data.user) {
        throw new Error("Usuário não encontrado");
      }

      // Check if user exists in our custom users table  
      const userId = data.user.id;

      const {
        data: userData,
        error: userError,
      } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (userError && userError.code !== 'PGRST116') {
        await supabase.auth.signOut();
        throw userError;
      }

      let profile = userData;

      if (!profile) {
        const fallbackProfile = {
          id: userId,
          email: data.user.email ?? email,
          role: 'viewer',
        };

        const {
          data: createdProfile,
          error: createError,
        } = await supabase
          .from('users')
          .insert(fallbackProfile)
          .select('*')
          .maybeSingle();

        if (createError) {
          await supabase.auth.signOut();
          throw new Error('Usuário não tem permissão de acesso ao painel');
        }

        profile = createdProfile ?? fallbackProfile;
      }

      return { success: true, user: data.user, userProfile: profile };
    } catch (error: any) {
      console.error("Login error:", error);
      return { 
        success: false, 
        error: error.message || "Erro ao fazer login" 
      };
    }
  },

  // Logout the current user
  logout: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      console.error("Logout error:", error);
      return { 
        success: false, 
        error: error.message || "Erro ao fazer logout" 
      };
    }
  },

  // Get the current session
  getSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        throw error;
      }

      return { success: true, session };
    } catch (error: any) {
      console.error("Get session error:", error);
      return { 
        success: false, 
        error: error.message || "Erro ao recuperar sessão" 
      };
    }
  }
};
